import type {
  BoardColumn,
  BoardEvent,
  BoardExternalBusyInterval,
  EmployeeOption,
} from "../../../../../_logic/calendar-types"
import { localDayKey, zonedDayKey } from "../../../../../_logic/overview-move"

export type OverviewEmployeeDay = {
  daySummary?: BoardColumn["daySummary"]
  employee: EmployeeOption
  events: BoardEvent[]
  busy: BoardExternalBusyInterval[]
  dayOff: boolean
}
export type OverviewDay = {
  key: string
  date: Date
  included: boolean
  employees: OverviewEmployeeDay[]
}

export const buildOverviewWeeks = (
  columns: BoardColumn[],
  visibleDays: Date[],
): OverviewDay[][] => {
  if (!visibleDays.length) return []

  const selected = new Set(visibleDays.map(localDayKey))
  const employees = [
    ...new Map(
      columns
        .filter((column) => column.employee)
        .map((column) => [column.employee!.id, column.employee!]),
    ).values(),
  ]
  const byColumn = new Map(
    columns.map((column) => [`${localDayKey(column.day)}:${column.employee?.id}`, column]),
  )
  const first = new Date(visibleDays[0])
  first.setHours(12, 0, 0, 0)
  first.setDate(first.getDate() - ((first.getDay() + 6) % 7))
  const last = new Date(visibleDays[visibleDays.length - 1])
  last.setHours(12, 0, 0, 0)
  last.setDate(last.getDate() + ((7 - last.getDay()) % 7))
  const weeks: OverviewDay[][] = []
  let week: OverviewDay[] = []
  for (const date = new Date(first); date <= last; date.setDate(date.getDate() + 1)) {
    const key = localDayKey(date)
    week.push({
      key,
      date: new Date(date),
      included: selected.has(key),
      employees: employees.map((employee) => {
        const column = byColumn.get(`${key}:${employee.id}`)

        return {
          daySummary: column?.daySummary,
          employee,
          dayOff: Boolean(column?.availability?.dayOff),
          events: [...(column?.timedEvents ?? [])].sort(
            (a, b) =>
              a.start.getTime() - b.start.getTime() ||
              String(a.event.id).localeCompare(String(b.event.id)),
          ),
          busy: column?.externalBusyIntervals ?? [],
        }
      }),
    })

    if (week.length === 7) {
      weeks.push(week)
      week = []
    }
  }

  return weeks
}

export const isOverviewToday = (key: string, now: Date, timezone: string) =>
  key === zonedDayKey(now, timezone)
